import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import YeniRezervasyonModal from '../../components/YeniRezervasyonModal';
import RezervasyonDetayModal from '../../components/RezervasyonDetayModal';
import DatePicker from '../../components/DatePicker';
import { apiRequest } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import UsageInfo from '../../components/UsageInfo';

// ── Durum Renkleri ve Config ──
const DEFAULT_COLORS = {
  'Temiz': '#dcfce7',
  'Dolu': '#cffafe',
  'Kirli': '#f3f4f6',
  'Rezerve': '#fef9c3',
  'Arızalı': '#fee2e2',
  'KirliArızalı': '#334155', // Hem kirli hem arızalı için varsayılan koyu gri
};

const darkenHex = (hex, factor = 0.72) => {
  const num = parseInt((hex || '#ffffff').replace('#', ''), 16);
  const r = Math.min(255, Math.floor(((num >> 16) & 0xff) * factor));
  const g = Math.min(255, Math.floor(((num >> 8) & 0xff) * factor));
  const b = Math.min(255, Math.floor((num & 0xff) * factor));
  return `#${[r, g, b].map(c => c.toString(16).padStart(2, '0')).join('')}`;
};

const formatTarih = (isoStr) => {
  if (!isoStr) return '—';
  const d = new Date(isoStr);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
};

const bugunFmt = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
};

const parseDate = (str) => {
  if (!str) return null;
  const [g, a, y] = str.split('.').map(Number);
  return new Date(y, a - 1, g);
};

// ── Misafir Liste Modal (Premium & Centered) ──
function MisafirListeModal({ baslik, rezervasyonlar, tarih, onClose, onDetay }) {
  const [fRezNo, setFRezNo] = useState('');
  const [fMisafir, setFMisafir] = useState('');
  const [fOda, setFOda] = useState('');
  const [gunSayisi, setGunSayisi] = useState(30);
  const [sadeceSeciliGun, setSadeceSeciliGun] = useState(true);
  const [sortKol, setSortKol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  function toggleSort(kol) {
    if (sortKol === kol) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKol(kol); setSortDir('asc'); }
  }

  function SortIcon({ kol }) {
    if (sortKol !== kol) return <span className="ml-1 opacity-25 text-[8px]">⇅</span>;
    return <span className="ml-1 text-indigo-300 text-[8px]">{sortDir === 'asc' ? '▲' : '▼'}</span>;
  }

  const filtrelenmis = rezervasyonlar.filter(r => {
    // Arama filtreleri (her zaman devrede)
    const misafirAdi = r.rezervasyonMisafirler?.map(m => m.misafir?.adSoyad).filter(Boolean).join(' ') || '';
    if (fRezNo && !r.rezNo?.toLocaleLowerCase('tr-TR').includes(fRezNo.toLocaleLowerCase('tr-TR'))) return false;
    if (fMisafir && !misafirAdi.toLocaleLowerCase('tr-TR').includes(fMisafir.toLocaleLowerCase('tr-TR'))) return false;
    if (fOda && !String(r.odaNo || '').toLowerCase().includes(fOda.toLowerCase())) return false;

    // Gün filtresi (KARTLARLA BİREBİR AYNI MANTIK)
    if (sadeceSeciliGun) {
      const t = parseDate(tarih);
      // UTC'ye çevirmeden yerel tarih stringi oluştur (timezone kaymasını önler)
      const [tg, ta, ty] = tarih.split('.');
      const tISO = `${ty}-${ta}-${tg}`;
      const toLocalISO = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      const g = new Date(r.girisTarihi);
      const c = r.cikisTarihi ? new Date(r.cikisTarihi) : (t ? new Date(t.getTime() + 86400000) : null);

      if (baslik.includes('Konaklayan')) {
        return r.durum === 'C-IN Yapıldı' && t >= new Date(g.setHours(0, 0, 0, 0)) && t < new Date(c?.setHours(0, 0, 0, 0));
      }
      if (baslik.includes('Gelecek')) {
        return r.durum !== 'İptal' && r.durum !== 'C-IN Yapıldı' && r.durum !== 'C-OUT Yapıldı' && toLocalISO(g) >= tISO;
      }
      if (baslik.includes('Ayrılan')) {
        return r.durum === 'C-OUT Yapıldı' && c && toLocalISO(c) === tISO;
      }
    }

    // Tüm Zamanlar Modu: Sadece kategorisine göre
    if (baslik.includes('Konaklayan')) return r.durum === 'C-IN Yapıldı';
    if (baslik.includes('Gelecek')) return r.durum !== 'İptal' && r.durum !== 'C-IN Yapıldı' && r.durum !== 'C-OUT Yapıldı';
    if (baslik.includes('Ayrılan')) return r.durum === 'C-OUT Yapıldı';

    return true;
  });

  const sorted = [...filtrelenmis].sort((a, b) => {
    if (!sortKol) return 0;
    let va, vb;
    if (sortKol === 'rezNo') { va = a.rezNo || ''; vb = b.rezNo || ''; }
    else if (sortKol === 'misafir') { va = (a.rezervasyonMisafirler?.[0]?.misafir?.adSoyad || ''); vb = (b.rezervasyonMisafirler?.[0]?.misafir?.adSoyad || ''); }
    else if (sortKol === 'odaNo') { va = String(a.odaNo || ''); vb = String(b.odaNo || ''); }
    else if (sortKol === 'giris') { va = a.girisTarihi || ''; vb = b.girisTarihi || ''; }
    else if (sortKol === 'durum') { va = a.durum || ''; vb = b.durum || ''; }
    else { va = ''; vb = ''; }
    // numeric: true olmadan "10" < "2" sayılıyor; oda numaraları yanlış sıralanıyordu
    const cmp = va.localeCompare(vb, 'tr', { numeric: true });
    return sortDir === 'asc' ? cmp : -cmp;
  });

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[500] flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
      <div className="bg-white w-full max-w-5xl rounded-[2.5rem] shadow-2xl border border-white/20 overflow-hidden animate-modal-in max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-8 py-6 bg-gradient-to-r from-indigo-600 to-violet-600 shrink-0">
          <div>
            <h3 className="text-xl font-black text-white tracking-widest uppercase">{baslik}</h3>
            <p className="text-indigo-200 text-[10px] font-black uppercase tracking-widest mt-1">
              {sadeceSeciliGun ? `${tarih} Tarihindeki Kayıtlar` : 'Tüm Zamanlar'} · {filtrelenmis.length} Rezervasyon Bulundu
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="bg-white/10 p-1.5 rounded-2xl flex items-center gap-1 border border-white/10">
              <button
                onClick={() => setSadeceSeciliGun(true)}
                className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${sadeceSeciliGun ? 'bg-white text-indigo-600 shadow-lg' : 'text-white/60 hover:text-white'}`}>
                Seçili Gün
              </button>
              <button
                onClick={() => setSadeceSeciliGun(false)}
                className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${!sadeceSeciliGun ? 'bg-white text-indigo-600 shadow-lg' : 'text-white/60 hover:text-white'}`}>
                Tüm Zamanlar
              </button>
            </div>
            <button onClick={onClose} className="w-12 h-12 rounded-2xl bg-white/10 hover:bg-white/20 flex items-center justify-center text-white text-xl transition-all">✕</button>
          </div>
        </div>
        <div className="p-8 overflow-y-auto custom-scrollbar flex-1">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            <input value={fRezNo} onChange={e => setFRezNo(e.target.value)} placeholder="REZERVASYON NO..." className="bg-slate-50 border-2 border-transparent focus:border-indigo-400 rounded-2xl px-5 py-3 text-xs font-black text-slate-700 outline-none uppercase tracking-widest" />
            <input value={fMisafir} onChange={e => setFMisafir(e.target.value)} placeholder="MİSAFİR ARA..." className="bg-slate-50 border-2 border-transparent focus:border-indigo-400 rounded-2xl px-5 py-3 text-xs font-black text-slate-700 outline-none uppercase tracking-widest" />
            <input value={fOda} onChange={e => setFOda(e.target.value)} placeholder="ODA NO..." className="bg-slate-50 border-2 border-transparent focus:border-indigo-400 rounded-2xl px-5 py-3 text-xs font-black text-slate-700 outline-none uppercase tracking-widest" />
          </div>
          <div className="rounded-[2rem] overflow-hidden border border-slate-100">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">
                  {[
                    { label: 'Rez No', kol: 'rezNo' },
                    { label: 'Misafir', kol: 'misafir' },
                    { label: 'Oda', kol: 'odaNo' },
                    { label: 'Giriş / Çıkış', kol: 'giris' },
                    { label: 'Durum', kol: 'durum' },
                  ].map(({ label, kol }) => (
                    <th key={kol} className="py-5 px-6 cursor-pointer select-none hover:text-indigo-500 transition-colors" onClick={() => toggleSort(kol)}>
                      <span className="inline-flex items-center gap-0.5">{label}<SortIcon kol={kol} /></span>
                    </th>
                  ))}
                  <th className="py-5 px-6 text-right">İşlem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {sorted.map(r => (
                  <tr key={r.rezervasyonId} className="hover:bg-slate-50 transition-colors">
                    <td className="py-4 px-6 font-black text-slate-400 text-xs tracking-widest">#{r.rezNo}</td>
                    <td className="py-4 px-6 font-black text-slate-700 text-xs uppercase leading-tight">{r.rezervasyonMisafirler?.map(m => m.misafir?.adSoyad).join(', ')}</td>
                    <td className="py-4 px-6"><span className="bg-indigo-50 text-indigo-600 px-3 py-1 rounded-xl text-[10px] font-black border border-indigo-100">{r.odaNo}</span></td>
                    <td className="py-4 px-6 text-[11px] font-bold text-slate-500 whitespace-nowrap">{formatTarih(r.girisTarihi)} - {formatTarih(r.cikisTarihi)}</td>
                    <td className="py-4 px-6">
                      <span className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest ${r.durum?.includes('C-IN') ? 'bg-emerald-100 text-emerald-700' : 'bg-indigo-100 text-indigo-700'}`}>{r.durum}</span>
                    </td>
                    <td className="py-4 px-6 text-right">
                      <button onClick={() => { onDetay(r.rezervasyonId); onClose(); }} className="bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-black uppercase tracking-widest px-4 py-2 rounded-xl shadow-lg shadow-indigo-100">DETAY</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Ayarlar Modal (Premium & Centered) ──
function AyarlarModal({ onClose, onKaydet, mevcutRenkler, mevcutBoyut }) {
  const [renkler, setRenkler] = useState({ ...mevcutRenkler });
  const [boyut, setBoyut] = useState(mevcutBoyut);

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[500] flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
      <div className="bg-white w-full max-w-lg rounded-[2.5rem] shadow-2xl border border-white/20 overflow-hidden animate-modal-in max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-8 py-6 bg-gradient-to-r from-indigo-600 to-violet-600">
          <h3 className="text-xl font-black text-white tracking-widest uppercase">Plan Ayarları</h3>
          <button onClick={onClose} className="w-12 h-12 rounded-2xl bg-white/10 hover:bg-white/20 flex items-center justify-center text-white text-xl transition-all">✕</button>
        </div>
        <div className="p-8 space-y-6 overflow-y-auto custom-scrollbar flex-1">
          <div>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Renk Ayarları</p>
            <div className="space-y-3">
              {[
                { key: 'Temiz', label: 'MÜSAİT' },
                { key: 'Rezerve', label: 'REZERVE' },
                { key: 'Kirli', label: 'KİRLİ' },
                { key: 'Arızalı', label: 'ARIZALI' },
                { key: 'KirliArızalı', label: 'KİRLİ & ARIZALI' },
                { key: 'Dolu', label: 'KONAKLIYOR' },
              ].map(item => (
                <div key={item.key} className="flex items-center gap-4 group">
                  <input type="color" value={renkler[item.key] || DEFAULT_COLORS[item.key]}
                    onChange={e => setRenkler(r => ({ ...r, [item.key]: e.target.value }))}
                    className="w-12 h-12 rounded-2xl border-4 border-slate-100 cursor-pointer p-0.5 flex-shrink-0 transition-transform group-hover:scale-110" />
                  <div className="flex-1 h-12 rounded-2xl border border-slate-100 flex items-center px-4 bg-slate-50 group-hover:bg-white group-hover:border-indigo-200 transition-all shadow-inner">
                    <span className="text-[10px] font-black text-slate-600 tracking-widest">{item.label}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Kart Boyutu</p>
            <div className="grid grid-cols-3 gap-2">
              {[
                { key: 'kucuk', label: 'KÜÇÜK', icon: '📱' },
                { key: 'orta', label: 'ORTA', icon: '💻' },
                { key: 'buyuk', label: 'BÜYÜK', icon: '🖥️' }
              ].map(b => (
                <button key={b.key} onClick={() => setBoyut(b.key)}
                  className={`flex flex-col items-center justify-center p-4 rounded-3xl border-2 transition-all gap-2 active:scale-95 ${boyut === b.key ? 'border-indigo-500 bg-indigo-50 text-indigo-700 shadow-lg shadow-indigo-100' : 'border-slate-50 bg-slate-50 text-slate-400 hover:border-slate-200'}`}>
                  <span className="text-2xl">{b.icon}</span>
                  <p className="text-[9px] font-black tracking-widest uppercase">{b.label}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="p-8 border-t border-slate-100 flex gap-3">
          <button onClick={() => { onKaydet({ renkler, boyut }); onClose(); }} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-5 rounded-[1.5rem] text-[10px] font-black tracking-[0.2em] shadow-xl shadow-indigo-100">KAYDET</button>
          <button onClick={onClose} className="px-8 bg-slate-100 hover:bg-slate-200 text-slate-600 py-5 rounded-[1.5rem] text-[10px] font-black tracking-widest">İPTAL</button>
        </div>
      </div>
    </div>
  );
}

// ── Oda Kartı (Premium & Responsive) ──
function OdaKarti({ oda, aktifRezervasyon, bekleyenRezervasyon, onDetay, onYeniGiris, boyut, adisyonToplam, setMenu, renkOverride }) {
  const cardRef = useRef(null);

  // Pasiflik Kontrolleri
  const katPasif = oda.odaGrubu?.aktifMi === false || oda.grupAktifMi === false;
  const tipPasif = oda.odaTipi?.aktifMi === false || oda.odaTipiAktifMi === false;
  const odaPasif = oda.aktifMi === false;
  const isRezKapali = katPasif || tipPasif || odaPasif;

  const getPasifNeden = () => {
    if (katPasif) return "KAT KAPALI";
    if (tipPasif) return "TİP KAPALI";
    if (odaPasif) return "ODA PASİF";
    return "KAPALI";
  };

  const effectiveDurum = aktifRezervasyon ? 'Dolu'
    : bekleyenRezervasyon ? 'Rezerve'
      : (oda.durum === 'Kirli' ? 'Kirli' : 'Temiz');

  const isKirliAriza = oda.arizaliMi && oda.durum === 'Kirli';
  const statusKey = isKirliAriza ? 'KirliArızalı' : (oda.arizaliMi ? 'Arızalı' : effectiveDurum);

  const bgBase = renkOverride?.[statusKey] || DEFAULT_COLORS[statusKey] || '#ffffff';
  const headerBase = darkenHex(bgBase, 0.85);

  const bk = {
    kucuk: { h: 'auto', text: 'text-[11px]', sub: 'text-[9px]', pad: 'p-3', icon: 'w-8 h-8' },
    orta: { h: '160px', text: 'text-sm', sub: 'text-[10px]', pad: 'p-5', icon: 'w-10 h-10' },
    buyuk: { h: '220px', text: 'text-lg', sub: 'text-xs', pad: 'p-7', icon: 'w-14 h-14' }
  }[boyut] || { h: '160px', text: 'text-sm', sub: 'text-[10px]', pad: 'p-5', icon: 'w-10 h-10' };

  const handleMenuClick = e => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setMenu({
      oda, aktifRezervasyon, bekleyenRezervasyon, isRezKapali,
      pos: { top: rect.bottom + 8, left: rect.left }
    });
  };

  return (
    <div ref={cardRef} onClick={() => !isRezKapali && aktifRezervasyon && onDetay(aktifRezervasyon.rezervasyonId)}
      className={`group relative rounded-[2.5rem] transition-all duration-300 transform-gpu hover:-translate-y-2 hover:shadow-2xl active:scale-95 select-none cursor-pointer overflow-hidden border-2 border-white/40`}
      style={{ minHeight: bk.h, backgroundColor: bgBase }}>

      {/* Shine & Glass Effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent pointer-events-none" />

      {/* Pasif Overlay */}
      {isRezKapali && (
        <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-[2px] z-20 flex flex-col items-center justify-center p-2 text-center">
          <span className="text-[10px] font-black text-white bg-slate-900/80 px-4 py-1.5 rounded-full shadow-2xl scale-110 tracking-[0.2em] mb-1">#KAPALI</span>
          <span className="text-[8px] font-black text-white/70 uppercase tracking-widest">{getPasifNeden()}</span>
        </div>
      )}

      {/* Header */}
      <div className="px-6 py-4 flex items-start justify-between relative z-10" style={{ backgroundColor: headerBase + '55' }}>
        <div className="flex flex-col min-w-0">
          <h4 className={`font-black tracking-tighter leading-none mb-1 text-slate-800 ${bk.text}`}>ODA {oda.odaNo}</h4>
          <span className={`font-black opacity-60 uppercase tracking-widest truncate text-slate-600 ${bk.sub}`}>{oda.odaTipiAd || 'STANDART'}</span>
        </div>
        <button onClick={handleMenuClick} className="w-8 h-8 rounded-xl bg-black/5 hover:bg-black/10 flex items-center justify-center transition-all text-slate-800 border border-black/5">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><circle cx="12" cy="12" r="1" /><circle cx="12" cy="5" r="1" /><circle cx="12" cy="19" r="1" /></svg>
        </button>
      </div>

      {/* Content */}
      <div className={`${bk.pad} relative z-10 flex flex-col justify-end min-h-[50%] pt-2`}>
        {aktifRezervasyon ? (
          <div className="space-y-2">
            <p className="font-black text-slate-800 tracking-tight truncate leading-tight uppercase text-xs">
              {aktifRezervasyon.rezervasyonMisafirler?.map(m => m.misafir?.adSoyad).join(', ') || '—'}
            </p>
            <div className="flex items-center justify-between gap-2">
              <span className="font-black text-[9px] bg-black/5 text-slate-600 px-2 py-1 rounded-lg">{(aktifRezervasyon.odaTutari || 0).toLocaleString('tr-TR')} ₺</span>
              {adisyonToplam > 0 && <span className="font-black text-[8px] text-rose-600">+{adisyonToplam} ₺ ADİSYON</span>}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-1.5">
            <span className={`font-black uppercase tracking-[0.2em] text-slate-500/80 ${bk.sub}`}>
              {statusKey === 'KirliArızalı' ? 'KİRLİ & ARIZALI' : (oda.arizaliMi ? 'ARIZALI' : (oda.durum === 'Kirli' ? 'KİRLİ' : 'MÜSAİT'))}
            </span>
            {bekleyenRezervasyon && (
              <div className="flex items-center gap-1.5 bg-white/40 px-3 py-1.5 rounded-xl border border-white/60 w-fit backdrop-blur-sm">
                <span className="animate-pulse w-1.5 h-1.5 rounded-full bg-amber-500" />
                <span className="text-[10px] font-black text-slate-700 tracking-tight uppercase leading-none">REZ: {formatTarih(bekleyenRezervasyon.girisTarihi)}</span>
              </div>
            )}
            {!isRezKapali && !oda.arizaliMi && !bekleyenRezervasyon && (
              <button onClick={e => { e.stopPropagation(); onYeniGiris(oda); }}
                className="mt-2 w-fit bg-slate-800 hover:bg-black text-white px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all shadow-lg active:scale-95">GİRİŞ YAP</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Ana Sayfa (Premium RoomRack) ──
export default function RoomRack() {
  const { hasYetki } = useAuth();
  const [tarih, setTarih] = useState(bugunFmt());
  const [aktifModal, setAktifModal] = useState(null);
  const [ayarlarAcik, setAyarlarAcik] = useState(false);
  const [yeniGirisOda, setYeniGirisOda] = useState(null);
  const [detayRezId, setDetayRezId] = useState(null);
  const [renkOverride, setRenkOverride] = useState({});
  const [boyut, setBoyut] = useState('orta');
  const [activeMenu, setActiveMenu] = useState(null);
  const [arizaOda, setArizaOda] = useState(null);
  const [arizaNot, setArizaNot] = useState('');
  const [arizaKaydediliyor, setArizaKaydediliyor] = useState(false);

  const [odaGruplari, setOdaGruplari] = useState([]);
  const [odalar, setOdalar] = useState([]);
  const [rezervasyonlar, setRezervasyonlar] = useState([]);
  const [adisyonMap, setAdisyonMap] = useState({});
  const [odemeMap, setOdemeMap] = useState({});
  const [bugunTahsilat, setBugunTahsilat] = useState(0);
  const [odemeListesi, setOdemeListesi] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);

  // Arka plan kaydırmayı engelle (Scroll Lock - Gelişmiş & Gutter Fix)
  useEffect(() => {
    const isAnyModalOpen = aktifModal || ayarlarAcik || yeniGirisOda || detayRezId !== null || arizaOda;

    if (isAnyModalOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.scrollbarGutter = 'stable';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.scrollbarGutter = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.scrollbarGutter = '';
    };
  }, [aktifModal, ayarlarAcik, yeniGirisOda, detayRezId, arizaOda]);

  const veriYukle = useCallback(async () => {
    setYukleniyor(true);
    try {
      const [odaData, rezData, adisyonData, odemeData, ayarData] = await Promise.all([
        apiRequest('oda'),
        apiRequest('rezervasyonlar'),
        apiRequest('adisyonlar').catch(() => []),
        apiRequest('odemeler').catch(() => []),
        apiRequest('genel-ayarlar').catch(() => null)
      ]);

      // Renkleri yükle
      if (ayarData) {
        setRenkOverride({
          'Temiz': ayarData.rackMusaitRenk,
          'Kirli': ayarData.rackKirliRenk,
          'Arızalı': ayarData.rackArizaliRenk,
          'Rezerve': ayarData.rackGelecekRenk,
          'Dolu': ayarData.rackKonaklayanRenk,
          'KirliArızalı': ayarData.rackKirliArizaliRenk,
        });
        if (ayarData.rackKartBoyutu) setBoyut(ayarData.rackKartBoyutu);
      }

      setOdalar(odaData || []);
      setRezervasyonlar((rezData || []).map(r => ({ ...r, rezervasyonId: r.rezervasyonId || r.id })));

      const aMap = {}; (adisyonData || []).forEach(a => { if (a.rezervasyonId) aMap[a.rezervasyonId] = (aMap[a.rezervasyonId] || 0) + (a.toplamTutar || 0); });
      setAdisyonMap(aMap);

      const oMap = {};
      const _n = new Date();
      const bugunStr = `${_n.getFullYear()}-${String(_n.getMonth()+1).padStart(2,'0')}-${String(_n.getDate()).padStart(2,'0')}`;
      let tTahsilat = 0;
      (odemeData || []).forEach(o => {
        if (o.rezervasyonId) oMap[o.rezervasyonId] = (oMap[o.rezervasyonId] || 0) + (o.tutar || 0) * (o.kurDegeri || 1);
        // Bugünün tahsilatını hesapla (odemeTarihi alanını kontrol et)
        const tStr = o.odemeTarihi || o.tarih || '';
        if (tStr && tStr.startsWith(bugunStr)) {
          tTahsilat += (o.tutar || 0) * (o.kurDegeri || 1);
        }
      });
      setOdemeListesi(odemeData || []);
      setOdemeMap(oMap);
      setBugunTahsilat(tTahsilat);

      const grupMap = {};
      (odaData || []).forEach(o => {
        const gId = o.odaGrubuId || 0;
        if (!grupMap[gId]) grupMap[gId] = { ...o.odaGrubu, grupAdi: o.odaGrubuAd || 'GENEL', odalar: [] };
        grupMap[gId].odalar.push(o);
      });
      
      // Her grubun içindeki odaları "Natural Sort" ile sırala (Örn: Oda 1, Oda 2, ... Oda 10)
      Object.values(grupMap).forEach(g => {
        g.odalar.sort((a, b) => {
          const numA = String(a.odaNo || '');
          const numB = String(b.odaNo || '');
          return numA.localeCompare(numB, undefined, { numeric: true, sensitivity: 'base' });
        });
      });

      setOdaGruplari(Object.values(grupMap).sort((a, b) => (a.grupNo || 0) - (b.grupNo || 0)));
    } catch (err) { console.error(err); }
    setYukleniyor(false);
  }, []);

  useEffect(() => { veriYukle(); }, [veriYukle]);

  const parseTarihStr = (str) => {
    if (!str) return new Date();
    const [g, a, y] = str.split('.').map(Number);
    return new Date(y, a - 1, g);
  };

  const bugunStr = () => {
    const d = new Date();
    return [String(d.getDate()).padStart(2, '0'), String(d.getMonth() + 1).padStart(2, '0'), d.getFullYear()].join('.');
  };

  const aktifRezervasyonBul = (odaId, targetDateStr = tarih) => {
    const target = parseTarihStr(targetDateStr);
    return rezervasyonlar.find(r => {
      if (r.odaId !== odaId || r.durum === 'İptal') return false;
      const giris = new Date(r.girisTarihi);
      const girisSafe = new Date(giris.getFullYear(), giris.getMonth(), giris.getDate());

      let cikisSafe;
      if (r.cikisTarihi) {
        const cikis = new Date(r.cikisTarihi);
        cikisSafe = new Date(cikis.getFullYear(), cikis.getMonth(), cikis.getDate());
      } else {
        cikisSafe = new Date(target.getFullYear(), target.getMonth(), target.getDate() + 1);
      }

      return target >= girisSafe && target < cikisSafe;
    }) || null;
  };

  // Dinamik İstatistik Hesaplamaları (Tarih Değişimine Duyarlı)
  const stats = useMemo(() => {
    const t = parseDate(tarih);
    if (!t) return [];
    // UTC'ye çevirmeden yerel tarih stringi oluştur (timezone kaymasını önler)
    const [tg, ta, ty] = tarih.split('.');
    const tISO = `${ty}-${ta}-${tg}`;
    const toLocalISO = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    const tStr = tarih;

    // 1. Tahsilat: Seçili tarihteki ödemeler
    const tTahsilatValue = (odemeListesi || []).filter(o => {
      const d = (o.odemeTarihi || o.tarih || '').split('T')[0];
      return d === tISO;
    }).reduce((s, o) => s + (o.tutar || 0) * (o.kurDegeri || 1), 0);

    // 2. Bekleyen: Seçili tarihte otelde olan (C-IN) misafirlerin bakiyesi
    const tBekleyenValue = rezervasyonlar.reduce((total, r) => {
      const g = new Date(r.girisTarihi);
      const c = r.cikisTarihi ? new Date(r.cikisTarihi) : new Date(t.getTime() + 86400000);
      const inHouse = r.durum === 'C-IN Yapıldı' && t >= new Date(g.setHours(0, 0, 0, 0)) && t < new Date(c.setHours(0, 0, 0, 0));

      if (inHouse) {
        const oda = (r.odaTutari || 0) * (r.kurDegeri || 1);
        const adisyon = adisyonMap[r.rezervasyonId] || 0;
        const odeme = odemeMap[r.rezervasyonId] || 0;
        const bakiye = (oda + adisyon) - odeme;
        return total + (bakiye > 0 ? bakiye : 0);
      }
      return total;
    }, 0);

    // 3. Konaklayan Sayısı: SADECE C-IN durumundakiler
    const tKonaklayanCount = rezervasyonlar.filter(r => {
      const g = new Date(r.girisTarihi);
      const c = r.cikisTarihi ? new Date(r.cikisTarihi) : new Date(t.getTime() + 86400000);
      return r.durum === 'C-IN Yapıldı' && t >= new Date(g.setHours(0, 0, 0, 0)) && t < new Date(c.setHours(0, 0, 0, 0));
    }).length;

    // 4. Gelecek Sayısı: Seçili tarih ve sonrasında Check-In bekleyen rezervasyonlar
    const tGelecekCount = rezervasyonlar.filter(r => {
      const g = new Date(r.girisTarihi);
      return r.durum !== 'İptal' && r.durum !== 'C-IN Yapıldı' && r.durum !== 'C-OUT Yapıldı' && toLocalISO(g) >= tISO;
    }).length;

    // 5. Ayrılan Sayısı: SADECE seçili tarihte çıkışı olan (C-OUT)
    const tAyrilanCount = rezervasyonlar.filter(r => {
      const c = r.cikisTarihi ? new Date(r.cikisTarihi) : null;
      return r.durum === 'C-OUT Yapıldı' && c && toLocalISO(c) === tISO;
    }).length;

    return [
      { label: 'MÜSAİT', v: odalar.filter(o => o.aktifMi && !o.arizaliMi && o.durum !== 'Kirli' && !aktifRezervasyonBul(o.odaId, tStr)).length, c: 'from-emerald-400 to-green-600', i: '✨' },
      { label: 'KONAKLAYAN', v: tKonaklayanCount, c: 'from-blue-500 to-indigo-700', i: '🛌', action: () => setAktifModal('konaklayan') },
      { label: 'GELECEK', v: tGelecekCount, c: 'from-amber-400 to-yellow-600', i: '⚡', action: () => setAktifModal('gelecek') },
      { label: 'AYRILDI', v: tAyrilanCount, c: 'from-cyan-500 to-blue-600', i: '🚪', action: () => setAktifModal('ayrilan') },
      { label: 'TAHSİLAT', v: tTahsilatValue.toLocaleString('tr-TR') + ' ₺', c: 'from-violet-500 to-purple-700', i: '💰' },
      { label: 'BEKLEYEN', v: tBekleyenValue.toLocaleString('tr-TR') + ' ₺', c: 'from-rose-500 to-orange-600', i: '🧾' },
      { label: 'KİRLİ / ARIZA', v: odalar.filter(o => o.durum === 'Kirli' || o.arizaliMi).length, c: 'from-slate-600 to-slate-800', i: 'Sweep' },
    ];
  }, [tarih, odalar, rezervasyonlar, adisyonMap, odemeMap, odemeListesi]);

  return (
    <div className="p-4 sm:p-8 space-y-8 animate-fade-in relative">

      {/* Header & Stats Dashboard */}
      <div className="bg-white/95 backdrop-blur-xl rounded-[3rem] p-8 sm:p-10 shadow-2xl border border-white/40 overflow-hidden group">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8 relative z-10">
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-slate-900 rounded-[2rem] flex items-center justify-center text-white text-3xl shadow-2xl animate-bounce-slow">🏢</div>
            <div>
              <h1 className="text-3xl font-black text-slate-800 tracking-tighter uppercase leading-none mb-3">Oda Planı</h1>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{tarih} Tarihli Genel Bakış</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button onClick={() => setTarih(bugunStr())} className="h-14 px-5 bg-white border-2 border-slate-100 hover:border-indigo-400 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all">BUGÜN</button>
            <div className="flex items-center bg-slate-50 rounded-2xl border-2 border-transparent focus-within:border-indigo-400 p-1 transition-all">
              <DatePicker value={tarih} onChange={setTarih} className="bg-transparent border-0 text-[11px] font-black text-slate-700 w-32 text-center uppercase tracking-widest outline-none" />
            </div>
            <button onClick={veriYukle} className="h-14 px-8 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-indigo-100 active:scale-95 transition-all">YENİLE</button>
            <button onClick={() => setAyarlarAcik(true)} className="w-14 h-14 bg-white border-2 border-slate-100 hover:border-indigo-400 rounded-2xl flex items-center justify-center shadow-lg transition-all text-xl">⚙️</button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mt-12">
          {stats.map(s => (
            <button key={s.label} onClick={s.action} disabled={!s.action} className={`bg-gradient-to-br ${s.c} p-5 rounded-[2rem] text-white shadow-xl text-left relative overflow-hidden group transition-all ${s.action ? 'active:scale-95 hover:shadow-2xl' : 'cursor-default'}`}>
              <span className="absolute top-2 right-2 opacity-20 text-2xl">{s.i}</span>
              <p className="text-[8px] font-black opacity-60 uppercase tracking-widest mb-1">{s.label}</p>
              <h3 className="text-xl font-black tracking-tighter">{s.v}</h3>
            </button>
          ))}
        </div>
      </div>

      {/* Oda Grupları / Katlar */}
      <div className="space-y-12">
        {yukleniyor ? (
          <div className="bg-white/50 backdrop-blur-sm rounded-[3rem] p-24 text-center border-2 border-dashed border-slate-200">
            <div className="text-5xl mb-4 animate-spin-slow">⭐</div>
            <p className="text-slate-400 font-black text-xs uppercase tracking-widest">Veriler yükleniyor...</p>
          </div>
        ) : odaGruplari.map(grup => (
          <div key={grup.odaGrubuId} className="space-y-6">
            <div className="flex items-center gap-4 ml-6">
              <div className={`w-3 h-10 rounded-full ${grup.aktifMi === false ? 'bg-slate-300' : 'bg-indigo-500'}`} />
              <h2 className={`text-2xl font-black tracking-tighter uppercase ${grup.aktifMi === false ? 'text-slate-400' : 'text-slate-800'}`}>
                {grup.grupAdi} {grup.aktifMi === false && <span className="text-[10px] italic opacity-50 lowercase ml-2">#kapalı</span>}
              </h2>
              <div className="flex-1 h-px bg-slate-100" />
            </div>
            <div className="grid gap-6" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${boyut === 'kucuk' ? '120px' : boyut === 'buyuk' ? '260px' : '180px'}, 1fr))` }}>
              {grup.odalar.map(oda => (
                <OdaKarti key={oda.odaId} boyut={boyut} oda={oda} aktifRezervasyon={aktifRezervasyonBul(oda.odaId)} bekleyenRezervasyon={rezervasyonlar.find(r => r.odaId === oda.odaId && r.durum !== 'İptal' && r.durum !== 'C-OUT Yapıldı' && r.durum !== 'C-IN Yapıldı')} adisyonToplam={adisyonMap[aktifRezervasyonBul(oda.odaId)?.rezervasyonId] || 0} setMenu={setActiveMenu} onYeniGiris={setYeniGirisOda} onDetay={setDetayRezId} renkOverride={renkOverride} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Global Dropdown Menu (Portal) */}
      {activeMenu && createPortal(
        <>
          <div className="fixed inset-0 z-[1000]" onClick={() => setActiveMenu(null)} />
          <div className="fixed bg-white/95 backdrop-blur-xl rounded-[2rem] shadow-2xl border border-slate-100 p-1.5 z-[1001] w-48 animate-modal-in" style={{ top: activeMenu.pos.top, left: Math.min(activeMenu.pos.left, window.innerWidth - 200) }}>
            <div className="flex flex-col">
              {/* Müsait odalar için hızlı giriş butonu */}
              {/* Giriş yap butonu: Sadece Temiz veya Kirli odalarda, Arızalı değilse ve Rezervasyon yoksa gözükmeli */}
              {(activeMenu.oda.durum === 'Temiz' || activeMenu.oda.durum === 'Kirli') && !activeMenu.oda.arizaliMi && !activeMenu.bekleyenRezervasyon && !activeMenu.aktifRezervasyon && (
                <button onClick={() => { setActiveMenu(null); setYeniGirisOda(activeMenu.oda); }}
                  className="w-full text-left px-4 py-3.5 text-[9.5px] font-black text-indigo-600 hover:bg-slate-50 rounded-2xl flex items-center gap-3 uppercase tracking-widest border-b border-slate-50 whitespace-nowrap overflow-hidden text-ellipsis">
                  🚪 Giriş Yap
                </button>
              )}
              {/* Rezervasyon detay butonu: SADECE içeride bir misafir varsa (aktif veya bekleyen) gözükmeli */}
              {(activeMenu.aktifRezervasyon || activeMenu.bekleyenRezervasyon) ? (
                <button onClick={() => { setActiveMenu(null); activeMenu.aktifRezervasyon ? setDetayRezId(activeMenu.aktifRezervasyon.rezervasyonId) : (activeMenu.bekleyenRezervasyon && setDetayRezId(activeMenu.bekleyenRezervasyon.rezervasyonId)); }}
                  className="w-full text-left px-4 py-3.5 text-[9.5px] font-black text-slate-700 hover:bg-slate-50 rounded-2xl flex items-center gap-3 uppercase tracking-widest border-b border-slate-50 whitespace-nowrap overflow-hidden text-ellipsis">📋 Res: Detay</button>
              ) : null}
              <button onClick={() => { setActiveMenu(null); apiRequest(`oda/${activeMenu.oda.odaId}/durum`, 'PATCH', { durum: activeMenu.oda.durum === 'Kirli' ? 'Temiz' : 'Kirli' }).then(veriYukle); }}
                className="w-full text-left px-4 py-3.5 text-[9.5px] font-black text-slate-700 hover:bg-slate-50 rounded-2xl flex items-center gap-3 uppercase tracking-widest border-b border-slate-50 whitespace-nowrap overflow-hidden text-ellipsis">🛏️ {activeMenu.oda.durum === 'Kirli' ? 'TEMİZLE' : 'KİRLET'}</button>
              <button onClick={() => {
                if (activeMenu.oda.arizaliMi) {
                  apiRequest(`oda/${activeMenu.oda.odaId}/arizali`, 'PATCH', { arizaliMi: false }).then(veriYukle);
                  setActiveMenu(null);
                } else {
                  setArizaOda(activeMenu.oda);
                  setArizaNot('');
                  setActiveMenu(null);
                }
              }} className={`w-full text-left px-4 py-3.5 text-[9.5px] font-black rounded-2xl flex items-center gap-3 uppercase tracking-widest whitespace-nowrap overflow-hidden text-ellipsis ${activeMenu.oda.arizaliMi ? 'text-emerald-600 hover:bg-emerald-50' : 'text-rose-600 hover:bg-rose-50'}`}>
                {activeMenu.oda.arizaliMi ? '🛠️ Arıza Onar' : '🔧 Arıza Yap'}
              </button>
            </div>
            {activeMenu.isRezKapali && <div className="px-4 py-3 text-[8.5px] font-black text-rose-500 uppercase tracking-widest italic opacity-60 text-center">İŞLEM YAPILAMAZ</div>}
          </div>
        </>,
        document.body
      )}

      {/* Modals (Portal Rendered & Centered) */}
      {aktifModal === 'gelecek' && createPortal(<MisafirListeModal baslik="Gelecek Misafirler" rezervasyonlar={rezervasyonlar} tarih={tarih} onClose={() => setAktifModal(null)} onDetay={setDetayRezId} />, document.body)}
      {aktifModal === 'konaklayan' && createPortal(<MisafirListeModal baslik="Konaklayan Misafirler" rezervasyonlar={rezervasyonlar} tarih={tarih} onClose={() => setAktifModal(null)} onDetay={setDetayRezId} />, document.body)}
      {aktifModal === 'ayrilan' && createPortal(<MisafirListeModal baslik="Ayrılan Misafirler" rezervasyonlar={rezervasyonlar} tarih={tarih} onClose={() => setAktifModal(null)} onDetay={setDetayRezId} />, document.body)}
      {ayarlarAcik && createPortal(<AyarlarModal onClose={() => setAyarlarAcik(false)} mevcutRenkler={renkOverride} mevcutBoyut={boyut} onKaydet={({ renkler, boyut: b }) => { apiRequest('genel-ayarlar/rack-renkleri', 'PUT', { rackMusaitRenk: renkler['Temiz'], rackKirliRenk: renkler['Kirli'], rackArizaliRenk: renkler['Arızalı'], rackKonaklayanRenk: renkler['Dolu'], rackGelecekRenk: renkler['Rezerve'], rackKirliArizaliRenk: renkler['KirliArızalı'], rackKartBoyutu: b }).then(veriYukle); }} />, document.body)}
      {yeniGirisOda && createPortal(<YeniRezervasyonModal onClose={() => setYeniGirisOda(null)} varsayilanOda={yeniGirisOda.odaId} onKaydedildi={() => { setYeniGirisOda(null); veriYukle(); }} />, document.body)}
      {detayRezId && createPortal(<RezervasyonDetayModal rezervasyonId={detayRezId} onClose={() => setDetayRezId(null)} onGuncelle={veriYukle} />, document.body)}

      {/* Arıza Açıklama Modalı (Portal) */}
      {arizaOda && createPortal(
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[600] flex items-center justify-center p-4 animate-fade-in" onClick={() => setArizaOda(null)}>
          <div className="bg-white w-full max-w-md rounded-[2.5rem] shadow-2xl overflow-hidden animate-modal-in" onClick={e => e.stopPropagation()}>
            <div className="px-8 py-6 bg-rose-600">
              <h3 className="text-xl font-black text-white tracking-widest uppercase">Arıza Açıklaması</h3>
              <p className="text-rose-200 text-[10px] font-black uppercase tracking-widest mt-1">Oda {arizaOda.odaNo} · Arıza Kaydı Oluşturuluyor</p>
            </div>
            <div className="p-8">
              <textarea
                autoFocus
                value={arizaNot}
                onChange={e => setArizaNot(e.target.value)}
                placeholder="ARIZA NEDENİNİ BURAYA YAZINIZ..."
                className="w-full h-32 bg-slate-50 border-2 border-transparent focus:border-rose-400 rounded-2xl px-5 py-4 text-xs font-black text-slate-700 outline-none uppercase tracking-widest resize-none mb-6"
              />
              <div className="flex gap-3">
                <button onClick={() => setArizaOda(null)} className="flex-1 px-6 py-4 rounded-2xl border-2 border-slate-100 text-[10px] font-black text-slate-400 uppercase tracking-widest hover:bg-slate-50 transition-all">İptal</button>
                <button
                  disabled={arizaKaydediliyor}
                  onClick={() => {
                    setArizaKaydediliyor(true);
                    apiRequest(`oda/${arizaOda.odaId}/arizali`, 'PATCH', { arizaliMi: true, arizaNedeni: arizaNot })
                      .then(() => {
                        setArizaOda(null);
                        setArizaKaydediliyor(false);
                        veriYukle();
                      });
                  }}
                  className="flex-1 px-6 py-4 rounded-2xl bg-rose-600 hover:bg-rose-700 text-white shadow-xl shadow-rose-100 text-[10px] font-black uppercase tracking-widest transition-all disabled:opacity-50"
                >
                  {arizaKaydediliyor ? 'KAYDEDİLİYOR...' : 'ARIZALI YAP'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      <UsageInfo
        id="usage-room-rack"
        color="slate"
        title="Nasıl Kullanılır?"
        description="Oda Planı, otelinizin anlık fiziksel doluluk ve operasyonel durumunu yönettiğiniz ana kumanda panelidir."
        scenarios={[
          { title: "Yeni Giriş Yapma", content: "Müsait durumdaki yeşil odalara tıklayarak veya kart üzerindeki 'Giriş Yap' butonu ile hızlıca yeni rezervasyon açabilirsiniz." },
          { title: "Oda Durumlarını Güncelleme", content: "Odaların sağ üstündeki menüden odayı Kirli veya Arızalı olarak işaretleyebilir, operasyonel süreçleri takip edebilirsiniz." },
          { title: "Pasif Odalar", content: "Katı, oda tipini veya odayı pasif yaptığınızda, bu odalar otomatik olarak rezervasyona kapatılır ve '#pasif' olarak işaretlenir." }
        ]}
      />

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in { animation: fadeIn 0.4s ease-out forwards; }
        .animate-modal-in { animation: fadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes bounceSlow { 0%, 100% { transform: translateY(-5%); } 50% { transform: none; } }
        .animate-bounce-slow { animation: bounceSlow 3s infinite; }
        .animate-spin-slow { animation: spin 8s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #cbd5e1; }
      `}</style>
    </div>
  );
}